/**
 * Telemetry Configuration
 *
 * Controls the client-side error beacon. The library's failure paths swallow
 * exceptions so a fault can never block navigation; this is how those silent
 * catches become a signal.
 *
 * @module config/telemetry
 */
/**
 * Beacon path, resolved relative to the mount root that served the library.
 *
 * The library is served either from `cl-attr.<brand>/latest/cl-attr.min.js` or
 * first-party from `www.<brand>/cl-attr/latest/cl-attr.min.js`. Resolving
 * `../e` against the script's own URL yields `/e` and `/cl-attr/e`
 * respectively, so one rule covers both mounts.
 */
export const TELEMETRY_BEACON_RELATIVE_PATH = '../e';
/**
 * Fraction of eligible errors reported, 0..1.
 *
 * 1 by default: this is a health mechanism for all traffic, and the expected
 * volume is a few hundred events a day against a 20M/month Workers Logs
 * allowance. Lower it only if a pathological template starts erroring on every
 * click.
 */
export const TELEMETRY_SAMPLE_RATE = 1;
/**
 * Maximum beacons per pageview. Guards against a loop turning one broken page
 * into thousands of log events.
 */
export const TELEMETRY_MAX_PER_PAGE = 5;
/** Maximum characters sent for an error message. */
export const TELEMETRY_MAX_MESSAGE_LENGTH = 200;
/**
 * Maximum characters sent for the comma-joined href parameter names.
 *
 * Unbounded, a page with many query keys could push the beacon URL past what
 * sendBeacon or an intermediary accepts, and it would fail silently — the exact
 * failure mode this telemetry exists to eliminate.
 */
export const TELEMETRY_MAX_PARAM_NAMES_LENGTH = 300;
/** Maximum characters sent for a stack trace (request body, not the URL). */
export const TELEMETRY_MAX_STACK_LENGTH = 1800;
/**
 * Query parameter that triggers one synthetic beacon at load.
 *
 * Every reporting path sits behind a defensive catch that is deliberately hard
 * to trigger from outside, so without this there is no way to prove the chain
 * (library -> beacon -> worker -> Workers Logs) actually connects short of
 * waiting for a real production fault. Gated on the parameter, so it never
 * fires for real users, and it doubles as a permanent post-deploy smoke test.
 */
export const TELEMETRY_SELF_TEST_PARAM = 'cl-attr-test-beacon';
/**
 * Global opt-out. Setting `window.__clAttrDisableTelemetry = true` before the
 * library loads suppresses all beacons — a kill switch that needs no deploy.
 */
export const TELEMETRY_DISABLE_FLAG = '__clAttrDisableTelemetry';
/**
 * Link parameters whose VALUES may be reported, not just their names.
 *
 * The names-only default exists because most attribution params are
 * pseudonymous per-user identifiers. These are different: they identify an
 * offer, a campaign or a creative, which is what makes a failure pattern
 * recognisable — "every failure is offer_id 4099" is actionable, "some offer
 * failed" is not.
 *
 * Deliberately excluded, and they must stay excluded: clreqid, cl_system_id,
 * aff_unique1 (request ids), aff_unique5 and tblci (the ad network's per-click
 * user id), transaction_id, kbid, gclid/msclkid/gbraid/wbraid (per-user click
 * ids), and ZipCode (user location). Anything not on this list is reported as a
 * name only.
 */
export const TELEMETRY_VALUE_ALLOWLIST = [
    'offer_id',
    'aff_id',
    'url_id',
    'aff_sub',
    'aff_sub2',
    'aff_sub3',
    'aff_sub4',
    'aff_sub5',
    'cl_system',
    'aff_unique2',
    'aff_unique3',
    'aff_unique4',
];
/** Max characters of allow-listed key=value pairs sent per beacon. */
export const TELEMETRY_MAX_PARAM_VALUES_LENGTH = 400;
/**
 * Window in which an identical error is reported only once.
 *
 * One fault surfaces at more than one layer: appendParams catches it and
 * reports `linkEnhancement`, then handleAffiliateActivation's outer catch sees
 * the same exception and reports `click`. Both are the same underlying failure,
 * and each consumed one of TELEMETRY_MAX_PER_PAGE — so a single broken link
 * burned two fifths of the page's budget and pushed out later, different faults.
 * Measured on a live page: one click produced four beacons.
 *
 * The first report wins, because the inner layer names the more specific site.
 */
export const TELEMETRY_DEDUPE_WINDOW_MS = 2000;
